import {TierPriceHint} from "./TierPriceHint";
import {UnitPriceChoice} from "./UnitPriceChoice";
import { useActiveUnitProducts } from "@/api/unitPricing";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { PiLineInput } from "@hisense/shared";
import { describe, isFallback, withDescription, type DescriptionLang } from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { formatMoney } from "@/domain/money";
import { parseMoneyMajor } from "@/lib/moneyInput";
import { QuotationProductImage } from "@/components/quotations/QuotationProductImage";
import { fxNoteParts } from "./fxNote";

/**
 * A draft line. The PI-only members are optional so quotations/POs keep using
 * the same draft shape, but a PI editor MUST round-trip them: the API rebuilds
 * every pi_items row from this payload, so a column that is not carried here is
 * ERASED on save. productId / mcq / msq have no cell in the grid for exactly
 * that reason — they are snapshots the product picker fills in.
 */
export interface LineDraft {
  unitSelection?:import("@hisense/shared").UnitSelection;
  /**
   * pi_items.id khi đang sửa dòng có sẵn; rỗng với dòng mới thêm.
   *
   * Phải gửi ngược lên máy chủ, nếu không dòng nhận id mới sau mỗi lần lưu và
   * mọi phiếu đóng gói trỏ tới nó qua originalItemId sẽ treo.
   */
  id?: string;
  productCode: string;
  description: string;
  /** Bản tiếng Hoa, chụp cùng lúc với bản tiếng Anh. */
  descriptionZh: string;
  color: string;
  size: string;
  unit: string;
  quantity: string;
  unitPrice: string; // major units, as typed
  productId?: string;
  subRef?: string;
  customerStyleNo?: string;
  moq?: string;
  mcq?: string;
  msq?: string;
  dtdPrice?: string;
  exwPrice?: string;
  underMoqPrice?: string;
  /** Supplier material batch. Only collected when the parent PI is P-Process. */
  lotNumber?: string;
  /**
   * Ảnh của sản phẩm trên dòng — CHỈ để hiện (yêu cầu v1.8 mục 4.4), không gửi lên máy chủ. Gõ tay đổi mã thì ảnh bỏ đi (null):
   * không bao giờ hiện ảnh của một sản phẩm khác với mã đang ghi trên dòng.
   */
  imageUrl?: string | null;
  /** Lần quy đổi tiền tệ VỪA áp trong hộp Thêm sản phẩm (yêu cầu v1.8 mục 6.4) — gửi lên để máy chủ kiểm và ghi vào bản chụp dòng. */
  fxConversion?: PiLineInput["fxConversion"];
  /** Lần quy đổi đã LƯU trên PI (đọc từ bản chụp dòng) — chỉ để hiện, không gửi lại. */
  fxApplied?: import("@hisense/shared").LineFxSnapshot;
}

export function blankLine(): LineDraft {
  return { productCode: "", description: "", descriptionZh: "", color: "", size: "", unit: "gross", quantity: "", unitPrice: "" };
}

/** Optional cells are undefined until typed; every read goes through this. */
const v = (s: string | undefined) => s ?? "";

type NumField = "quantity" | "unitPrice" | "moq" | "mcq" | "msq" | "dtdPrice" | "exwPrice" | "underMoqPrice";

/** Optional integer cell: "" → undefined, junk → an error the caller reports. */
function optInt(raw: string | undefined): { value?: number; invalid?: boolean } {
  const s = v(raw).trim();
  if (s === "") return {};
  if (!/^\d+$/.test(s)) return { invalid: true };
  return { value: parseInt(s, 10) };
}

/** Optional money cell: "" → undefined, junk → invalid (never coerced to 0). */
function optMoney(raw: string | undefined): { value?: number; invalid?: boolean } {
  if (v(raw).trim() === "") return {};
  const p = parseMoneyMajor(v(raw));
  if (p.invalid || p.minor === undefined) return { invalid: true };
  return { value: p.minor };
}

/** Convert drafts → API line inputs (minor units). Returns {error} if a row is invalid. */
export function draftsToInput(lines: LineDraft[]):
  | { items: PiLineInput[]; total: number }
  | { error: { line: number; field: NumField } } {
  const items: PiLineInput[] = [];
  let total = 0;
  for (const [i, l] of lines.entries()) {
    if (!l.description.trim() && !l.productCode.trim()) continue; // skip blank rows
    const qty = Number(l.quantity);
    if (!/^\d+$/.test(l.quantity.trim()) || !Number.isSafeInteger(qty) || qty <= 0) return { error: { line: i + 1, field: "quantity" } };
    const price = parseMoneyMajor(l.unitPrice);
    if (price.invalid || price.minor === undefined) return { error: { line: i + 1, field: "unitPrice" } };

    const moq = optInt(l.moq);
    if (moq.invalid) return { error: { line: i + 1, field: "moq" } };
    for (const field of ["mcq", "msq"] as const) { if (optInt(l[field]).invalid) return { error: { line: i + 1, field } }; }
    const dtd = optMoney(l.dtdPrice);
    if (dtd.invalid) return { error: { line: i + 1, field: "dtdPrice" } };
    const exw = optMoney(l.exwPrice);
    if (exw.invalid) return { error: { line: i + 1, field: "exwPrice" } };
    const underMoq = optMoney(l.underMoqPrice);
    if (underMoq.invalid) return { error: { line: i + 1, field: "underMoqPrice" } };

    total += qty * price.minor;
    items.push({
      unitSelection:l.unitSelection,
      // Gửi lại id để dòng giữ nguyên danh tính qua mỗi lần lưu; thiếu nó thì
      // máy chủ đúc UUID mới và mọi phiếu đóng gói trỏ tới dòng sẽ treo.
      id: l.id || undefined,
      productId: l.productId || undefined,
      productCode: l.productCode || undefined,
      lotNumber: v(l.lotNumber).trim() || undefined,
      subRef: v(l.subRef) || undefined,
      customerStyleNo: v(l.customerStyleNo) || undefined,
      description: l.description || undefined,
      descriptionZh: l.descriptionZh || undefined,
      color: l.color || undefined,
      size: l.size || undefined,
      unit: l.unit || undefined,
      quantity: qty,
      moq: moq.value,
      mcq: optInt(l.mcq).value,
      msq: optInt(l.msq).value,
      unitPrice: price.minor,
      dtdPrice: dtd.value,
      exwPrice: exw.value,
      underMoqPrice: underMoq.value,
      fxConversion: l.fxConversion,
    });
  }
  return { items, total };
}

/** Thành tiền một dòng theo ô đang gõ (ô sai = 0) — dùng cho cột Thành tiền và khối tổng tiền của trang. */
export function lineAmount(l: LineDraft): number {
  const qty = parseInt(l.quantity || "0", 10) || 0;
  const p = parseMoneyMajor(l.unitPrice || "0");
  return p.minor !== undefined ? qty * p.minor : 0;
}
export const linesSubtotal = (lines: readonly LineDraft[]) => lines.reduce((s, l) => s + lineAmount(l), 0);

const inputCls =
  "h-8 w-full rounded-xs border border-line bg-surface px-1.5 text-xs text-ink outline-none focus:border-brand";
/** Frozen against horizontal scroll — the PI grid is wider than the viewport. */
const pinCls = "sticky left-0 z-10 bg-surface";

export function LineItemsEditor({
  lines,
  onChange,
  currency = "USD",
  variant = "basic",
  unitOptions = [],
  showLot = false,
  lotSuggestions = [],
  customerId,
  footer,
  lang = "en",
  showSide = true,
  hideTotals = false,
  framed = true,
}: {
  lines: LineDraft[];
  onChange: (lines: LineDraft[]) => void;
  /** Chứng từ đang in bằng tiếng gì — ô mô tả sửa đúng bản đó. */
  lang?: DescriptionLang;
  currency?: string;
  /** "pi" adds the columns proforma invoices persist (sub-ref, unit, MOQ, alt prices). */
  variant?: "basic" | "pi";
  unitOptions?: string[];
  /** Show the LOT column — the parent PI has p_process set. */
  showLot?: boolean;
  /** Khách của chứng từ — để gợi ý giá biết giá riêng của khách có thắng bậc không. */
  customerId?: string;
  /**
   * Lots already used elsewhere on THIS document, offered as suggestions.
   *
   * The lot is hand-typed and is the key that joins a shipment to its test
   * reports, so a single mistyped character silently breaks the traceability
   * chain the whole SOP exists to guarantee. Suggesting what is already on the
   * document makes the second and third line reuse the exact same string
   * without preventing a genuinely new lot from being typed.
   */
  lotSuggestions?: string[];
  /** Extra total rows (upcharge / LESS % / grand total) below the subtotal.
   *  Receives the rendered column count so its cells can span the grid. */
  footer?: (colCount: number) => React.ReactNode;
  /**
   * PI: hiện cột phụ (mã mẫu khách hàng, giá EXW, giá dưới MOQ, giá DTD). Trang PI tính: có dòng dùng thì luôn hiện
   * (piSideColumnsUsed), không thì theo nút "Hiện cột phụ" (19/09/2026).
   */
  showSide?: boolean;
  /** Trang tự vẽ khối tổng tiền dưới bảng: bảng chỉ còn nút Thêm dòng ở chân. */
  hideTotals?: boolean;
  /** false: bảng nằm trong một thẻ nhóm đã có viền — bỏ viền riêng của bảng. */
  framed?: boolean;
}) {
  const { t } = useTranslation();
  const pi = variant === "pi";
  // Nút "Đơn vị & giá" chỉ ở dòng mà mã có hồ sơ quy đổi đã duyệt (19/09/2026 — chốt UOM-R01..R03).
  const activeUnits = useActiveUnitProducts(lines.map((l) => l.productId ?? ""));
  const set = (i: number, patch: Partial<LineDraft>) =>
    onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const add = () => onChange([...lines, blankLine()]);
  const remove = (i: number) => onChange(lines.filter((_, j) => j !== i));

  const amount = lineAmount;
  const total = linesSubtotal(lines);

  // Cột phụ của PI (19/09/2026): mã mẫu khách hàng, giá EXW, giá dưới MOQ, giá DTD — trang PI quyết định hiện hay ẩn.
  const side = pi && showSide;
  // PI có thêm cột ảnh sản phẩm (yêu cầu v1.8 mục 4.4): nhìn ảnh mà soát đúng mã / quy cách lúc nhập.
  const colCount = 9 + (pi ? 4 : 0) + (side ? 4 : 0) + (showLot ? 1 : 0);
  /*
   * PI: độ rộng CỐ ĐỊNH từng cột (table-fixed), cột mô tả lấy phần còn lại, tối thiểu 16rem. Trước đây bảng tự chia bề rộng
   * nên ô mô tả bị bóp còn vài chữ ("Woven ma…") trong khi ô mã phụ thừa chỗ. Bảng rộng hơn khung thì cuộn ngang, cột mã
   * dính bên trái.
   */
  // Cột phụ nằm SAU Thành tiền (19/09/2026, soi trên production): đặt giữa thì bật cột phụ là cột Thành tiền bị đẩy ra
  // ngoài màn hình. Nút xoá dòng dính mép phải để luôn bấm được khi bảng cuộn ngang.
  // Có dòng mang đơn giá quy đổi thì cột đơn giá rộng ra để ghi chú "từ USD 0.2280 / 1 USD = 6.6 RMB" không bị bóp vỡ chữ.
  const priceWidth = lines.some((l) => l.fxConversion || l.fxApplied) ? 9.5 : 6;
  const widths: (number | null)[] = pi
    ? [2, 3.5, 8, 6.5, ...(showLot ? [9] : []), null, 7, 6.5, 5.5, 6, 5, priceWidth, 7, ...(side ? [7, 6, 6, 6] : []), 2]
    : [];
  const pinRight = pi ? "sticky right-0 z-10 bg-surface" : "";
  const minRem = widths.reduce<number>((sum, w) => sum + (w ?? 16), 0);

  return (
    <div className={framed ? "overflow-x-auto rounded-md border border-line bg-surface" : "overflow-x-auto bg-surface"}>
      {showLot && lotSuggestions.length > 0 && (
        <datalist id="lot-suggestions">
          {lotSuggestions.map((lot) => (
            <option key={lot} value={lot} />
          ))}
        </datalist>
      )}
      <table className={pi ? "w-full table-fixed border-collapse text-sm" : "w-full border-collapse text-sm"} style={pi ? { minWidth: `${minRem}rem` } : undefined}>
        {pi && (
          <colgroup>
            {widths.map((w, i) => <col key={i} style={w ? { width: `${w}rem` } : undefined} />)}
          </colgroup>
        )}
        <thead>
          <tr className="[&>th]:border-b [&>th]:border-line [&>th]:bg-sunken/60 [&>th]:px-2 [&>th]:py-1.5 [&>th]:text-2xs [&>th]:font-semibold [&>th]:uppercase [&>th]:tracking-wide [&>th]:text-ink-faint">
            <th className="w-8 text-right">#</th>
            {pi && <th className="text-left">{t("common.image")}</th>}
            <th className={pi ? `text-left ${pinCls} bg-sunken` : "text-left"}>{t("common.code")}</th>
            {pi && <th className="text-left">{t("common.subRef")}</th>}
            {showLot && <th className="w-40 text-left">{t("lot.number")}</th>}
            <th className="text-left">{t("common.description")} · {t(`common.printLanguage_${lang}`)}</th>
            <th className="text-left">{t("common.color")}</th>
            <th className="text-left">{t("common.size")}</th>
            <th className="w-20 text-right">{t("common.qty")}</th>
            {pi && <th className="w-24 text-left">{t("common.unit")}</th>}
            {pi && <th className="w-20 text-right">{t("common.moq")}</th>}
            <th className="w-24 text-right">{t("common.unitPrice")}</th>
            <th className="w-28 text-right">{t("common.amount")}</th>
            {side && <th className="text-left">{t("common.customerStyleNo")}</th>}
            {side && <th className="w-24 text-right">{t("common.exwPrice")}</th>}
            {side && <th className="w-24 text-right">{t("common.underMoqPrice")}</th>}
            {side && <th className="w-24 text-right">{t("common.dtdPrice")}</th>}
            <th className={pi ? `w-8 ${pinRight} bg-sunken` : "w-8"} />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="[&>td]:border-b [&>td]:border-line [&>td]:px-1.5 [&>td]:py-1">
              <td className="text-right font-mono text-2xs text-ink-faint">{String(i + 1).padStart(2, "0")}</td>
              {pi && (
                <td>
                  {/* Ảnh lấy từ hồ sơ sản phẩm của dòng; dòng không có ảnh hiện ô "No image", không bao giờ mượn ảnh sản phẩm khác. */}
                  <QuotationProductImage
                    compact
                    product={{ productCode: l.productCode, subRef: v(l.subRef), description: l.description, color: l.color, size: l.size, imageUrl: l.imageUrl ?? null }}
                  />
                </td>
              )}
              <td className={pi ? pinCls : undefined}>
                <input aria-label={`${t("common.code")} ${i + 1}`} className={`${inputCls} font-mono`} value={l.productCode} onChange={(e) => set(i, { productCode: e.target.value, ...(l.imageUrl ? { imageUrl: null } : {}) })} />
                {l.productId&&activeUnits.has(l.productId)&&<UnitPriceChoice key={`${l.productId}:${currency}`} productId={l.productId} currency={currency} unit={l.unit} quantity={l.quantity} unitPrice={l.unitPrice} onApply={choice=>set(i,{...choice,exwPrice:"",dtdPrice:"",underMoqPrice:"",fxConversion:undefined,fxApplied:undefined})}/>}
              </td>
              {pi && <td><input aria-label={`${t("common.subRef")} ${i + 1}`} className={`${inputCls} font-mono`} value={v(l.subRef)} onChange={(e) => set(i, { subRef: e.target.value })} /></td>}
              {showLot && (
                <td>
                  <input
                    className={`${inputCls} font-mono`}
                    value={v(l.lotNumber)}
                    onChange={(e) => set(i, { lotNumber: e.target.value })}
                    list="lot-suggestions"
                    placeholder={t("lot.placeholder")}
                    aria-label={`${t("lot.number")} ${i + 1}`}
                  />
                </td>
              )}
              <td><input aria-label={`${t("common.description")} ${i + 1}`} className={inputCls} value={lang === "zh" ? l.descriptionZh : l.description}
                title={(lang === "zh" ? l.descriptionZh : l.description) || undefined} placeholder={isFallback(l, lang) ? describe(l, lang) : undefined}
                onChange={(e) => set(i, withDescription(l, lang, e.target.value))} /></td>
              <td><input aria-label={`${t("common.color")} ${i + 1}`} className={inputCls} value={l.color} onChange={(e) => set(i, { color: e.target.value })} /></td>
              <td><input aria-label={`${t("common.size")} ${i + 1}`} className={inputCls} value={l.size} onChange={(e) => set(i, { size: e.target.value })} /></td>
              <td><input aria-label={`${t("common.qty")} ${i + 1}`} className={`${inputCls} text-right font-mono`} inputMode="numeric" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
                {pi && l.productId && (
                  <TierPriceHint productId={l.productId} customerId={customerId} quantity={l.quantity} unit={l.unit} unitPrice={l.unitPrice} currency={currency}
                    onApply={(price) => set(i, { unitPrice: price, fxConversion: undefined, fxApplied: undefined })} />
                )}</td>
              {pi && (
                <td>
                  {unitOptions.length ? (
                    <Select
                      containerClassName="h-8 w-full min-w-[5.5rem]"
                      className="text-xs"
                      value={l.unit}
                      onChange={(e) => set(i, { unit: e.target.value,unitPrice:"",exwPrice:"",dtdPrice:"",underMoqPrice:"",fxConversion:undefined,fxApplied:undefined })}
                    >
                      {!unitOptions.includes(l.unit) && <option value={l.unit}>{l.unit}</option>}
                      {unitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
                    </Select>
                  ) : (
                    <input aria-label={`${t("common.unit")} ${i + 1}`} className={inputCls} value={l.unit} onChange={(e) => set(i, { unit: e.target.value,unitPrice:"",exwPrice:"",dtdPrice:"",underMoqPrice:"",fxConversion:undefined,fxApplied:undefined })} />
                  )}
                </td>
              )}
              {pi && <td><input aria-label={`${t("common.moq")} ${i + 1}`} className={`${inputCls} text-right font-mono`} inputMode="numeric" value={v(l.moq)} onChange={(e) => set(i, { moq: e.target.value })} /></td>}
              <td><input aria-label={`${t("common.unitPrice")} ${i + 1}`} className={`${inputCls} text-right font-mono`} inputMode="decimal" value={l.unitPrice} onChange={(e) => set(i, { unitPrice: e.target.value, fxConversion: undefined, fxApplied: undefined })} />
                {/* Đơn giá đến từ quy đổi tiền tệ (v1.8 mục 6.4): ghi rõ tiền gốc, giá gốc, tỷ giá. Sửa tay giá là ghi chú này mất. */}
                {(l.fxConversion || l.fxApplied) && (() => {
                  const parts = fxNoteParts((l.fxConversion ?? l.fxApplied)!);
                  return (
                    <span className="mt-0.5 block text-right font-mono text-2xs leading-tight text-ink-soft" title={t("fxConvert.lineNoteTitle")}>
                      <span className="block font-sans">{t("fxConvert.lineNote", { note: parts.source })}</span>
                      <span className="block">{parts.rate}</span>
                    </span>
                  );
                })()}</td>
              <td className="text-right font-mono tabular text-ink">{formatMoney(amount(l), currency)}</td>
              {side && <td><input aria-label={`${t("common.customerStyleNo")} ${i + 1}`} className={inputCls} value={v(l.customerStyleNo)} onChange={(e) => set(i, { customerStyleNo: e.target.value })} /></td>}
              {side && <td><input aria-label={`${t("common.exwPrice")} ${i + 1}`} className={`${inputCls} text-right font-mono`} inputMode="decimal" value={v(l.exwPrice)} onChange={(e) => set(i, { exwPrice: e.target.value })} /></td>}
              {side && <td><input aria-label={`${t("common.underMoqPrice")} ${i + 1}`} className={`${inputCls} text-right font-mono`} inputMode="decimal" value={v(l.underMoqPrice)} onChange={(e) => set(i, { underMoqPrice: e.target.value })} /></td>}
              {side && <td><input aria-label={`${t("common.dtdPrice")} ${i + 1}`} className={`${inputCls} text-right font-mono`} inputMode="decimal" value={v(l.dtdPrice)} onChange={(e) => set(i, { dtdPrice: e.target.value })} /></td>}
              <td className={pi ? `text-center ${pinRight}` : "text-center"}>
                <button type="button" onClick={() => remove(i)} aria-label={t("common.removeLine")} className="text-ink-faint hover:text-danger">
                  <Trash2 className="size-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={colCount} className="px-2 py-1.5">
              <Button type="button" variant="secondary" size="sm" onClick={add}>
                <Plus className="size-3.5" /> {t("common.addLine")}
              </Button>
            </td>
          </tr>
          {hideTotals ? null : footer ? (
            <>
              <tr className="border-t border-line">
                <td colSpan={colCount - 2} className="px-2 py-1.5 text-right text-sm text-ink-soft">{t("common.subtotal")}</td>
                <td className="px-1.5 py-1.5 text-right font-mono tabular text-sm text-ink-soft">{formatMoney(total, currency)}</td>
                <td />
              </tr>
              {footer(colCount)}
            </>
          ) : (
            <tr className="border-t border-line">
              <td colSpan={colCount - 2} className="px-2 py-2 text-right text-sm font-medium text-ink">{t("common.total")} · {currency}</td>
              <td className="px-1.5 py-2 text-right font-mono tabular text-base font-semibold text-ink">{formatMoney(total, currency)}</td>
              <td />
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
}
